import { Link } from "@mui/material";

const StudySmart = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="img img--blog_img_caption" style={{ fontSize: 15, marginBottom: "10px" }}>
            &quot;If you want your people to be exceptional, you have to have an
            exceptional culture.&quot;
          </p>
          <p className="img img--blog_img_caption" style={{ fontSize: 13 }}>
            -Galen Emanuele-
          </p>
          <p className="text--post_paragraph">
            Have you heard? We've entered the age of the "
            <Link
              href="https://www.bbc.com/worklife/article/20210629-the-great-resignation-how-employers-drove-workers-to-quit"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about the great resignation"
            >
              great resignation
            </Link>
            " — the name given to the recent phenomena of large numbers of
            people quitting their jobs. Several factors are driving the trend
            and not wanting to be at a company with a bad culture is near the
            top of the list. This decentralized form of unionization has caught
            the attention of organizational leaders who are asking themselves,
            "how can we be an organization that attracts amazing people instead
            of repelling them?"
          </p>
          <h2 className="header header--page_title_h2_left">
            Great Culture Is the Answer
          </h2>
          <p className="text--post_paragraph">
            Culture has long been a favorite buzzword for consultants to
            describe the overall personality of an organization. According
            to&nbsp;
            <Link
              href="https://www.opensourcedworkplace.com/news/what-is-team-culture-in-the-workplace-five-ways-to-build-company-culture"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about team culture"
            >
              Open Sourced Workplace
            </Link>
            , team culture is how a group of people behave and think. It
            originates from the attitudes and belief systems they share and
            affects their ability to achieve goals and objectives.
          </p>
          <p className="text--post_paragraph">
            A company’s culture describes its overall tone and vibe. Warm and
            welcoming or cold and hostile? Are people empowered to work or
            limited by bureaucracy? And usually most important is do they feel
            that management and leadership have their back, care about them, and
            keep them informed of what’s going on and why?
          </p>
          <h2 className="header header--page_title_h2_left">
            Why Culture Matters
          </h2>
          <p className="text--post_paragraph">
            Today's best employees will not waste their efforts, time, or life
            using their talents at an organization whose values are not in line
            with their own. It's that simple. This is why it's imperative to
            actively cultivate a healthy culture at your organization. Which
            leads to the obvious question — how?
          </p>
          <h2 className="header header--page_title_h2_left">
            The 3 Tips for Creating a Winning Team Culture
          </h2>
          <p className="text--post_paragraph">
            Creating a team culture is almost a science at this point. Books
            have been written about it, annual conferences are devoted to it,
            and consultants specialize in it. And while those resources are
            helpful, you don’t need them to get started. We'll begin by covering
            the fundamentals with the hope that it'll inspire ideas on how to
            apply the concepts at your organization.
          </p>
          <div style={{ paddingLeft: "2rem" }}>
            <h3 className="header header--page_title_h3_left">
              1. Foster Trust In Everything
            </h3>
            <p className="text--post_paragraph">
              Without a doubt, trust is the most important ingredient in a
              healthy workplace culture.{" "}
              <Link
                href="https://fisher.osu.edu/blogs/leadreadtoday/building-a-culture-trust"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about building a culture trust"
              >
                Trust is the basis
              </Link>{" "}
              by which all relationships function. Its presence determines if
              people feel like they’re in a safe space to do their best work or
              a place where they need to keep their guard up.{" "}
              <Link
                href="https://www.huffpost.com/entry/teamwork-is-what-separates-the-good-from-great-companies_b_58e3c783e4b09deecf0e1a88"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about teamwork"
              >
                Michelle Burke
              </Link>
              , CEO of the Energy Catalyst Group, an organizational coaching
              firm, says teams that trust each other are happier, more
              productive, and more engaged in their work.
            </p>
            <p className="text--post_paragraph">
              An HBR article titled “
              <Link
                href="https://hbr.org/2017/01/the-neuroscience-of-trust"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about the neuroscience of trust"
              >
                The Neuroscience of Trust
              </Link>
              ” outlines how leaders can create a culture of trust in their
              organizations. And it probably isn’t surprising that being
              transparent, sharing information equally, recognizing excellence,
              and allowing people to perform without being micromanaged are all
              good habits.
            </p>
            <p className="text--post_paragraph">
              Another important part of building trust is establishing
              camaraderie personally as people. Regularly spending time together
              outside of work activities can facilitate that. Participate in
              team-building exercises. Have an outing. Get to know each other as
              humans — that's what we all are (at least until the robots take
              over).
            </p>
            <h3 className="header header--page_title_h3_left">
              2. Work Toward Your Goals
            </h3>
            <p className="text--post_paragraph">
              What's your purpose? What do you want to achieve? A clear, shared
              mission gives a team a sense of purpose and direction.{" "}
              <Link
                href="https://blog.jostle.me/blog/why-is-organizational-culture-important"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about why organizational culture is important"
              >
                Corey Moseley
              </Link>
              , an author specializing in company culture, says, "A strong
              organizational culture keeps core values front and center in all
              aspects of its operations and organizational structure."
            </p>
            <p className="text--post_paragraph">
              A disconnect between an organization's stated pursuits and where
              teams spend their time sends mixed signals, and inconsistency is
              like kryptonite to a healthy culture. Keep your stated purpose at
              the forefront and resist any attempts to introduce distractions,
              especially those that reek of busy work.
            </p>
            <p className="text--post_paragraph">
              If your company would benefit (and most would) from reviewing its
              goals from a fresh perspective, check out Simon Sinek’s book, "
              <Link
                href="https://simonsinek.com/product/start-with-why/"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about start with why"
              >
                Start With Why
              </Link>
              ." It provides a lot of practical insight to help you audit your
              processes to make sure you're spending time on the things that
              matter most.
            </p>
            <h3 className="header header--page_title_h3_left">
              3. Invest In Your People
            </h3>
            <p className="text--post_paragraph">
              Markets, industries, and the world are ever-changing, and
              organizations that want to lead the pack need to be agile and
              adapt. Ongoing education has to be a big part of that picture.
              Goal-oriented learning supports innovation, organizational growth,
              and employee retention.
            </p>
            <p className="text--post_paragraph">
              As a{" "}
              <Link
                href="http://www.bersin.com/Store/Details.aspx?id=12171"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
                aria-label="Read more about bersin"
              >
                Bersin & Associates report
              </Link>{" "}
              pointed out, "The single biggest driver of business impact is the
              strength of an organization's learning culture." A{" "}
              <Link
                href="https://www.cnbc.com/2019/02/27/94percent-of-employees-would-stay-at-a-company-for-this-one-reason.html"
                target="_blank"
                rel="noreferrer noopener nofollow sponsored"
              >
                Linkedin Learning
              </Link>{" "}
              report revealed, "94% of employees said investment in training and
              development is one of the major reasons they would decide to stay
              in a role for longer."
            </p>
            <p className="text--post_paragraph">
              Facilitate learning by building it into your operations. Send
              people to industry conferences, encourage your people to speak at
              them. Start a lunch-and-learn program. Bring in coaches and
              consultants. The possibilities are endless. Come together as a
              team and brainstorm the best ways to introduce new knowledge.
            </p>
          </div>
          <h2 className="header header--page_title_h2_left">
            Great Culture Doesn’t Happen By Accident
          </h2>
          <p className="text--post_paragraph">
            Promoting a healthy, positive, and productive culture requires
            ongoing effort but starting by emphasizing employee wellness,
            participating in team-building exercises (the fun ones), and
            creating processes that enable people to do their best work can lay
            a solid foundation upon which to build an authentic culture.
          </p>
          <h2 className="header header--page_title_h2_left">Other Resources</h2>
          <p className="text--post_paragraph">
            The{" "}
            <Link
              href="https://www.octanner.com/global-culture-report.html"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about global culture report"
            >
              Global Culture Report
            </Link>{" "}
            is a helpful resource to learn about current trends, statistics, and
            how employees feel about their workplace cultures. It has valuable
            information you can apply when creating the policies, procedures,
            environment, and other factors that shape the culture at your
            organization.
          </p>
          <p className="text--post_paragraph">
            And, perhaps most importantly, a great culture starts with great
            people who are passionate about the work they do, care about their
            teammates, and are enthusiastic about the opportunity in front of
            them. Finding those people can be challenging, but TeammateMe makes
            it easy to build the perfect team.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default StudySmart;
